import React, { useMemo } from 'react';

import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { BookingLedgerLock } from '@components/admin/booking/BookingLedgerLock';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInputOption } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table, TableCellPropsUnion } from '@components/Table/Table';
import {
  bookingsStrings,
  bookingsStringsCN,
  bookingStatusOptions,
  csManagerOptions,
  customsStatusOptions,
  customsReuqestStatusOptions,
  deliveryRequestStatusOptions,
  doStatusOptions,
  getInvoiceStatusOptions,
  getPaymentSlipStatusOptions,
  instructionStatusOptions,
  ledgerLockStatusOptions,
  moveOutStatusOptions,
  myCasesOptions,
  preAlertStatusOptions,
} from '@data/bookings';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { getInstructionStatusMeta } from '@data/instruction';
import { invoiceStrings } from '@data/invoice';
import { paymentSlipStrings } from '@data/paymentSlip';
import { preAlertStrings } from '@data/preAlert';
import { useSearch } from '@hooks/next/useSearch';
import {
  type CustomsStatusName,
  type CustomsRequestStatusName,
  getCustomsStatusLabel,
  getCustomsRequestStatusLabel,
} from '@models/next/CustomsRequestStatus';
import { type DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import { type DOStatusName } from '@models/next/DOStatus';
import { type InstructionStatusName } from '@models/next/Instruction';
import { type MoveOutStatusName } from '@models/next/MoveOutStatus';
import {
  type DashboardPreAlertQuery,
  DashboardPreAlertSearchType,
  mapPreAlertStatusValueToLable,
  type DashboardPreAlertListItem,
  PreAlertPathType,
  PreAlertLedgerLockStatus,
} from '@models/next/PreAlert';
import type { PaymentSlipStatus, InvoiceStatus } from '@models/next/StatusFlows';
import { User } from '@models/next/User';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { url } from '@utils/pages';
import {
  DEFAULT_PAGE_SIZE,
  formatDate,
  formatDateTime,
  formatISOString,
  formatPrice,
  mapOptional,
} from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  bookingsTableItem: {
    ...common.whitespacePre,
    '&$status': {
      width: 70,
      whiteSpace: 'initial',
    },
    '&$vessel': {
      width: 70,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&$isOverdue': {
      ...common.textError,
    },
  },
  status: {},
  vessel: {},
  isOverdue: {},
}));

type BookingItemKey =
  | keyof DashboardPreAlertListItem
  | 'link'
  | 'cargo_info'
  | 'invoice_tax_excluded_total_price'
  | 'payment_tax_excluded_total_price'
  | 'delivery_request_delivery_date'
  | 'delivery_request_pickup_date'
  | 'is_ledger_locked';

interface BookingsTableProps {
  queryParams: DashboardPreAlertQuery;
  columns: BookingItemKey[];
  user?: User | null;
  podOptions?: TextInputOption[];
  polOptions?: TextInputOption[];
  type: PreAlertPathType;
}

export const BookingsTable: React.FC<BookingsTableProps> = (props) => {
  const { queryParams, columns, user, podOptions = [], polOptions = [], type } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardPreAlertSearchType);
  const { checklist = [] } = search;

  const isFCChina = user ? new UserHelper(user).isFCChina : false;

  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert', {
    queries: {
      query: queryParams,
    },
  });

  const preAlerts = preAlert.data?.results ?? [];

  const isEmpty = preAlert.data?.count === 0;

  const itemIds = preAlerts.map((item) => item.d2d_no);

  const allChecked =
    preAlerts.length > 0 && preAlerts.every((item) => checklist.includes(item.d2d_no));

  const onChecklistChange = (next: string[]) => {
    updateSearch({ checklist: next });
  };

  const onHeadCheckableChange = () => {
    if (allChecked) {
      onChecklistChange(checklist.filter((id) => !itemIds.includes(id)));
    } else {
      onChecklistChange([...checklist, ...itemIds]);
    }
  };

  const onBodyCheckableChange = (d2d_no: string) => () => {
    if (checklist.includes(d2d_no)) {
      onChecklistChange(checklist.filter((checked) => checked !== d2d_no));
    } else {
      onChecklistChange([...checklist, d2d_no]);
    }
  };

  const bookingHeaderRenders: {
    [K in BookingItemKey]?: () => TableCellPropsUnion;
  } = {
    id: () => cellFactory.create(bookingsStrings.id),
    d2d_no: () => cellFactory.create(bookingsStrings.d2dNo),
    quantity: () => cellFactory.create(bookingsStrings.quantity),
    vessel: () => cellFactory.create(bookingsStrings.vessel),
    cargo_info: () => cellFactory.create(bookingsStrings.totalQuantityWeightMeasure),
    delivery_date: () =>
      cellFactory.createOrdering(bookingsStrings.deliveryDate, (value) =>
        updateSearch({
          ...search,
          page: 1,
          ordering: (value === 'DESC' ? '-' : '') + 'delivery_date',
        }),
      ),
    agency_name: () => cellFactory.create(bookingsStrings.agency),
    invoice_tax_excluded_total_price: () =>
      cellFactory.create(invoiceStrings.subsums.tax_excluded_total_price),
    payment_tax_excluded_total_price: () =>
      cellFactory.create(paymentSlipStrings.subsums.tax_excluded_total_price),
    profit: () => cellFactory.create(preAlertStrings.profit),
    shipper: () => cellFactory.create(bookingsStrings.shipper),
    consignee: () => cellFactory.create(bookingsStrings.consignee),
    cancel_detail: () =>
      cellFactory.create(isFCChina ? bookingsStringsCN.cancelReason : bookingsStrings.cancelReason),
    salesperson_japan_last_name: () =>
      cellFactory.createSelectable(bookingsStrings.csManagerJapan, csManagerOptions, (isMine) => {
        updateSearch({
          ...search,
          page: 1,
          salesperson_japan_id: isMine ? user?.id : undefined,
          has_salesperson_japan: isMine === null ? false : undefined,
        });
      }),
    salesperson_china_last_name: () =>
      cellFactory.createSelectable(bookingsStrings.csManagerChina, csManagerOptions, (isMine) => {
        updateSearch({
          ...search,
          page: 1,
          salesperson_china_id: isMine ? user?.id : undefined,
          has_salesperson_china: isMine === null ? false : undefined,
        });
      }),
    is_empty: () =>
      cellFactory.createSelectable(bookingsStrings.preAlert, preAlertStatusOptions, (value) => {
        updateSearch({
          ...search,
          page: 1,
          status: value as any,
        });
      }),
    status: () =>
      cellFactory.createSelectable(bookingsStrings.status, bookingStatusOptions, (value) => {
        updateSearch({
          ...search,
          page: 1,
          transport_status: value as string,
        });
      }),
    customs_instruction_status: () =>
      cellFactory.createSelectable(
        bookingsStrings.customsInstructionStatus(),
        instructionStatusOptions,
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            customs_instruction_status: [value as InstructionStatusName],
          });
        },
      ),
    delivery_instruction_status: () =>
      cellFactory.createSelectable(
        bookingsStrings.deliveryInstructionStatus(),
        instructionStatusOptions,
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            delivery_instruction_status: [value as InstructionStatusName],
          });
        },
      ),
    customs_status: () =>
      cellFactory.createSelectable(bookingsStrings.customsStatus, customsStatusOptions, (value) => {
        updateSearch({
          ...search,
          page: 1,
          customs_status: value as CustomsStatusName,
        });
      }),
    customs_request_status: () =>
      cellFactory.createSelectable(
        bookingsStrings.customsRequestStatus,
        customsReuqestStatusOptions,
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            customs_request_status: value as CustomsRequestStatusName,
          });
        },
      ),
    delivery_request_delivery_date: () =>
      cellFactory.createOrdering(deliveryRequestStrings.deliveryDate, (value) =>
        updateSearch({
          ...search,
          page: 1,
          ordering: (value === 'DESC' ? '-' : '') + 'delivery_request_delivery_date',
        }),
      ),
    delivery_request_pickup_date: () =>
      cellFactory.createOrdering(deliveryRequestStrings.pickupDate, (value) =>
        updateSearch({
          ...search,
          page: 1,
          ordering: (value === 'DESC' ? '-' : '') + 'delivery_request_pickup_date',
        }),
      ),
    delivery_request_status: () =>
      cellFactory.createSelectable(
        bookingsStrings.deliveryRequestStatus,
        deliveryRequestStatusOptions,
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            delivery_request_status: value as DeliveryRequestStatusName,
          });
        },
      ),
    salesperson_customs_last_name: () =>
      cellFactory.createSelectable(bookingsStrings.salespersonCustoms, myCasesOptions, (isMine) => {
        updateSearch({
          ...search,
          page: 1,
          member_salesperson_id: isMine ? user?.id : undefined,
        });
      }),
    do_status: () =>
      cellFactory.createSelectable(preAlertStrings.doStatus, doStatusOptions, (value) => {
        updateSearch({
          ...search,
          page: 1,
          do_status: [value as DOStatusName],
        });
      }),
    move_out_status: () =>
      cellFactory.createSelectable(preAlertStrings.moveOutStatus, moveOutStatusOptions, (value) => {
        updateSearch({
          ...search,
          page: 1,
          move_out_status: value as MoveOutStatusName,
        });
      }),
    etd: () =>
      cellFactory.createOrdering(bookingsStrings.etd, (value) => {
        updateSearch({
          ...search,
          page: 1,
          ordering: value === 'DESC' ? '-etd' : 'etd',
        });
      }),
    eta: () =>
      cellFactory.createOrdering(bookingsStrings.eta, (value) => {
        updateSearch({
          ...search,
          page: 1,
          ordering: value === 'DESC' ? '-eta' : 'eta',
        });
      }),
    pol: () =>
      cellFactory.createSelectable(
        bookingsStrings.pol,
        [...polOptions, { label: 'ALL', value: '' }],
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            pol: value as string,
          });
        },
      ),
    pod: () =>
      cellFactory.createSelectable(
        bookingsStrings.pod,
        [...podOptions, { label: 'ALL', value: '' }],
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            pod: value as string,
          });
        },
      ),

    invoice_status: () =>
      cellFactory.createSelectable(preAlertStrings.invoice, getInvoiceStatusOptions(), (value) => {
        updateSearch({
          ...search,
          page: 1,
          invoice_status: value as InvoiceStatus,
        });
      }),
    payment_slip_status: () =>
      cellFactory.createSelectable(
        preAlertStrings.paymentSlip,
        getPaymentSlipStatusOptions(),
        (value) => {
          updateSearch({
            ...search,
            page: 1,
            payment_slip_status: value as PaymentSlipStatus,
          });
        },
      ),
    member_salesperson_last_name: () =>
      cellFactory.createSelectable(bookingsStrings.memberSalesperson, myCasesOptions, (isMine) => {
        updateSearch({
          ...search,
          page: 1,
          member_salesperson_id: isMine ? user?.id : undefined,
        });
      }),
    is_ledger_locked: () =>
      cellFactory.createSelectable(
        preAlertStrings.is_ledger_locked,
        ledgerLockStatusOptions.filter((option) =>
          type === 'past_finished' ? option.value !== 'preparing' : true,
        ),
        (ledger_lock_status) => {
          updateSearch({
            ...search,
            page: 1,
            ledger_lock_status: ledger_lock_status as PreAlertLedgerLockStatus,
          });
        },
      ),
  };

  const mapBookingItem: (
    item: DashboardPreAlertListItem,
    index: number,
  ) => {
    [K in BookingItemKey]?: React.ReactNode;
  } = (item, index) => {
    const isOverdue =
      item.etd && !item.is_empty && !item.is_manually_locked
        ? dayjs(formatISOString(item.etd, '00:00', 'Asia/Tokyo')).isBefore(dayjs())
        : false;

    const renderItem = (
      children: React.ReactNode,
      options: { variant?: 'status' | 'vessel' } = {},
    ) => (
      <div
        className={clsx(
          classes.bookingsTableItem,
          isOverdue && classes.isOverdue,
          options.variant && classes[options.variant],
        )}
      >
        {children}
      </div>
    );

    const invoice_tax_excluded_total_price = item.profit?.invoice?.tax_excluded_total_price;
    const payment_tax_excluded_total_price = item.profit?.payment?.tax_excluded_total_price;

    return {
      id: renderItem(
        index + 1 + ((queryParams.page ?? 1) - 1) * (queryParams.page_size ?? DEFAULT_PAGE_SIZE),
      ),
      d2d_no: renderItem(item.d2d_no || '-'),
      link: (
        <Link
          className={clsx(classes.link, isOverdue && classes.textErrorImportant)}
          to={`${url('/admin/booking/BookingDetails')}?d2dNo=${item.d2d_no}&type=${type}`}
        >
          {bookingsStrings.details}
        </Link>
      ),
      created_at: renderItem(item.created_at == null ? '-' : formatDateTime(item.created_at)),
      is_empty: renderItem(mapPreAlertStatusValueToLable(!!item.is_empty) || '-'),
      quantity: renderItem(item.is_empty ? '-' : `${item.quantity || '-'}`),
      etd: renderItem(item.is_empty ? '-' : formatDate(item.etd, 'short') || '-'),
      eta: renderItem(item.is_empty ? '-' : formatDate(item.eta, 'short') || '-'),
      delivery_request_pickup_date: renderItem(
        formatDate(item.delivery_request?.pickup_date, 'short') || '-',
      ),
      delivery_request_delivery_date: renderItem(
        formatDate(item.delivery_request?.delivery_date, 'short') || '-',
      ),
      pod: renderItem(item.pod.slice(0, 3) || '-'),
      pol: renderItem(item.pol.slice(0, 3) || '-'),
      vessel: renderItem(item.is_empty ? '-' : item.vessel || '-', { variant: 'vessel' }),
      consignee: renderItem(
        <div className={classes.textTrim} style={{ width: '7rem' }}>
          {item.consignee || '-'}
        </div>,
      ),
      shipper: renderItem(
        <div className={classes.textTrim} style={{ width: '7rem' }}>
          {item.shipper || '-'}
        </div>,
      ),
      salesperson_china_last_name: renderItem(item.salesperson_china_last_name || '-'),
      salesperson_japan_last_name: renderItem(item.salesperson_japan_last_name || '-'),
      customs_instruction_status: renderItem(
        getInstructionStatusMeta(item.customs_instruction_status ?? '未送信').shortAdminLabel,
      ),
      delivery_instruction_status: renderItem(
        getInstructionStatusMeta(item.delivery_instruction_status ?? '未送信').shortAdminLabel,
      ),
      member_salesperson_last_name: renderItem(item.member_salesperson_last_name || '-'),
      customs_status: renderItem(
        item.customs_status ? getCustomsStatusLabel(item.customs_status) : '-',
        { variant: 'status' },
      ),
      customs_request_status: renderItem(
        item.customs_request_status
          ? getCustomsRequestStatusLabel(item.customs_request_status)
          : '-',
        { variant: 'status' },
      ),
      delivery_request_status: renderItem(item.delivery_request_status || '-', {
        variant: 'status',
      }),
      do_status: renderItem(item.do_status || '-', { variant: 'status' }),
      move_out_status: renderItem(item.move_out_status || '-', { variant: 'status' }),
      invoice_status: renderItem(item.invoice_status || '-'),
      payment_slip_status: renderItem(item.payment_slip_status || '-'),
      salesperson_customs_last_name: renderItem(item.salesperson_customs_last_name || '-'),
      cargo_info: renderItem(
        item.is_empty
          ? '-'
          : `${item.quantity || '-'}PACKAGE/${Number(item.gross_weight) || '-'}KGS/${
              Number(item.measurement) || '-'
            }CBM`,
      ),
      delivery_date: renderItem(formatDate(item.delivery_date, 'short') || '-'),
      agency_name: renderItem(item.agency_name || '-'),
      invoice_tax_excluded_total_price: renderItem(
        mapOptional(invoice_tax_excluded_total_price, (p) => formatPrice(p, 'JPY')) ?? '-',
      ),
      payment_tax_excluded_total_price: renderItem(
        mapOptional(payment_tax_excluded_total_price, (p) => formatPrice(p, 'JPY')) ?? '-',
      ),
      profit: renderItem(
        mapOptional(invoice_tax_excluded_total_price, (invoice) =>
          mapOptional(payment_tax_excluded_total_price, (payment) =>
            formatPrice(invoice - payment, 'JPY'),
          ),
        ) ?? '-',
      ),
      status: renderItem(item.status || '-'),
      cancel_detail: renderItem(item.cancel_detail || '-'),
      is_ledger_locked: renderItem(<BookingLedgerLock item={item} />),
    };
  };

  const bookingItems = useMemo(
    () =>
      preAlerts.map((item, index) => {
        return mapBookingItem(item, index);
      }),
    [preAlerts],
  );

  return (
    <QueryContainer queries={[preAlert]}>
      <PageSizeForm />

      <div style={{ height: 16 }} />
      <Table columnFixedUntil={4} fixedWidths={[50, 50, 100, 60]}>
        <Table.Head>
          <Table.Row
            columns={[
              cellFactory.createCheckable(allChecked, onHeadCheckableChange),
              cellFactory.create(bookingsStrings.id),
              cellFactory.create(bookingsStrings.d2dNo),
              cellFactory.create(''),
              ...columns.map((key) => bookingHeaderRenders[key]?.() ?? cellFactory.createBlank()),
            ]}
          />
        </Table.Head>
        <Table.Body>
          {bookingItems?.map((item, index) => (
            <Table.Row
              key={index}
              columns={[
                cellFactory.createCheckable(
                  checklist.includes(itemIds[index]),
                  onBodyCheckableChange(itemIds[index]),
                ),
                cellFactory.create(item.id),
                cellFactory.create(item.d2d_no),
                cellFactory.create(item.link),
                ...columns.map((key) => cellFactory.create(item[key])),
              ]}
            />
          ))}
        </Table.Body>
      </Table>
      <div style={{ height: 36 }} />
      {!isEmpty && (
        <div className={clsx(classes.flexInline, classes.justifyEnd)}>
          <PaginationWithSearchQuery query={preAlert} pageSize={queryParams.page_size} />
        </div>
      )}
      {isEmpty && (
        <div className={clsx(classes.textAlignCenter, classes.middleText, classes.textGray400)}>
          {bookingsStrings.emptyHint}
        </div>
      )}
    </QueryContainer>
  );
};
